import {
  FormComboBox,
  FormController,
  FormInput,
  FormSelect,
  FormTextArea,
} from '@/components/form';
import CreatePurchaseOrder from '@/features/Procurement/CreatePurchaseOrder';
import { useAppSelector } from '@/state';
import { CoreApi, ProcurementApi, ReleasesApi } from '@/state/api';
import { ProcurementType, ReleasesType } from '@/types';
import { Box, Button, Flex, Separator, Text } from '@chakra-ui/react';
import { Card, PopoverLazy, toasterApi } from '@ui';
import { useState } from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import z from 'zod';
import { SelectedSupplierProps } from '../Orders/NewOrder/NewOrder.context';

type DraftOrderManageCardProps = {
  isNew?: boolean;
  selectedDraftOrderId?: number;
  setSlideNumber?: React.Dispatch<React.SetStateAction<number>>;
  selectedSupplier?: SelectedSupplierProps | null;
  setSelectedSupplier?: React.Dispatch<
    React.SetStateAction<SelectedSupplierProps | null>
  >;
  showCreatePoModal?: boolean;
  setShowCreatePoModal?: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
};

const DraftOrderManageCard = ({
  isNew = false,
  selectedDraftOrderId,
  setSlideNumber,
  selectedSupplier,
  setSelectedSupplier,
  showCreatePoModal,
  setShowCreatePoModal,
  onClose,
}: DraftOrderManageCardProps) => {
  const currentUser = useAppSelector((state) => state.auth.current);
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );

  const [draftOrderDataForPO, setDraftOrderDataForPO] =
    useState<DraftOrdersFormType | null>(null);

  const [postDraftOrdersMutation, { isLoading: isLoadingPostDraftOrders }] =
    ProcurementApi.Api.usePostDraftOrdersMutation();

  const [
    postDraftOrderLinesMutation,
    { isLoading: isLoadingPostDraftOrderLines },
  ] = ProcurementApi.Api.usePostDraftOrderLinesMutation();

  const { data: allReleases } = ReleasesApi.Api.useGetAllReleaseQuery();
  const filteredReleasesByProject = allReleases?.filter(
    (release) => release.project === selectedProject?.id,
  );
  const { data: getPartsListByProjectAndReleaseQuery } =
    CoreApi.Api.useGetPartsListByProjectAndReleaseQuery({});

  const handleSubmitDraftOrdersData = async (formData: DraftOrdersFormType) => {
    if (formData.draftOrderSubmissionType === 'saveDraft') {
      try {
        let draftOrderId: number | undefined = selectedDraftOrderId;
        if (isNew) {
          const draftOrderResponse = await postDraftOrdersMutation({
            body: formData.draftOrder as ProcurementType.DraftOrders.Post['body'],
          }).unwrap();

          draftOrderId = draftOrderResponse.id;
        }

        if (draftOrderId && formData?.draftOrderLines?.length) {
          postDraftOrderLinesMutation({
            body: formData?.draftOrderLines?.map((line) => ({
              needed_by: line.needed_by,
              notes: line.notes,
              qty: line.qty,
              unit_price: line.unit_price,
              // Release is optional (project-level POs have none); only send it
              // when the user actually picked one.
              ...(line.release ? { release: line.release } : {}),
              ...(line.part_type === 'system'
                ? { part: line.part! }
                : { manual_part_number: line.manual_part_number! }),
            })),
            params: { draft_id: draftOrderId },
          }).unwrap();
        }

        toasterApi({
          type: 'success',
          status: 201,
          code: 'Success',
          message: 'Draft order saved successfully',
        });

        // reset state
        setSlideNumber?.(0);
        setSelectedSupplier?.(null);
        setShowCreatePoModal?.(false);
      } catch {
        toasterApi({
          type: 'error',
          status: 500,
          code: 'Error',
          message: 'Could not save draft order',
        });
      } finally {
        onClose?.();
      }
    }

    if (formData.draftOrderSubmissionType === 'createPo') {
      setDraftOrderDataForPO(formData);
      setShowCreatePoModal?.(true);
    }
  };

  return (
    <Card height="full">
      <Flex
        width="full"
        height="full"
        flexDirection="column"
        gapY={4}
        fontSize="sm"
        overflow="hidden"
      >
        <FormController
          key={`draft-order-form-${isNew}-${isLoadingPostDraftOrders}-${isLoadingPostDraftOrderLines}`}
          size="lg"
          formSchema={
            isNew ? draftOrdersFormSchemaPost : draftOrdersFormSchemaPatch
          }
          defaultValues={
            draftOrderDataForPO !== null
              ? draftOrderDataForPO
              : {
                  draftOrderSubmissionType: 'saveDraft',
                  draftOrder: isNew
                    ? {
                        project: selectedProject?.id as number,
                        supplier: selectedSupplier?.id as number,
                        created_by: currentUser?.userid as number,
                        status: 'DRAFT',
                        title: '',
                        function_number: '',
                      }
                    : null,
                  draftOrderLines: isNew
                    ? []
                    : [
                        {
                          part_type: 'system',
                          part: 0,
                          manual_part_number: '',
                          qty: 1,
                          unit_price: '1',
                          needed_by: '',
                          notes: '',
                          release: selectedRelease?.id as number,
                        },
                      ],
                }
          }
          onFormSubmit={handleSubmitDraftOrdersData}
          buttonProps={{ hidden: isNew ? true : false }}
        >
          {({
            register,
            control,
            errors,
            setValue,
            trigger,
          }: {
            register: UseFormRegister<DraftOrdersFormType>;
            control: Control<DraftOrdersFormType>;
            errors: FieldErrors<DraftOrdersFormType>;
            setValue?: UseFormReturn<DraftOrdersFormType>['setValue'];
            trigger?: UseFormReturn<DraftOrdersFormType>['trigger'];
          }) => {
            const { fields, append, remove } = useFieldArray({
              control,
              name: 'draftOrderLines',
            });

            const draftOrderLines = useWatch({
              control,
              name: 'draftOrderLines',
            });

            if (showCreatePoModal) {
              return (
                <CreatePurchaseOrder
                  isNew
                  onClickBack={() => setShowCreatePoModal?.(false)}
                  onSubmit={() => {
                    setShowCreatePoModal?.(false);
                    setSlideNumber?.(0);

                    setDraftOrderDataForPO(null);
                  }}
                  draftOrder={draftOrderDataForPO?.draftOrder}
                  draftOrderLines={
                    draftOrderDataForPO?.draftOrderLines?.map((line) => ({
                      ...line,
                      ...(line?.part_type === 'system'
                        ? {
                            part_id: line?.part,
                            part_number:
                              getPartsListByProjectAndReleaseQuery?.find(
                                (part) => part.id === line?.part,
                              )?.part_number ?? '',
                          }
                        : {
                            part_id: null,
                            part_number: line?.manual_part_number ?? '',
                          }),
                    })) ?? []
                  }
                />
              );
            }

            return (
              <Flex>
                <Flex
                  width="full"
                  minHeight="200px"
                  flexDirection="column"
                  gapY={4}
                  overflow="hidden"
                >
                  {/* Top actions */}
                  <Flex justifyContent="space-between">
                    <Button
                      size="sm"
                      variant="outline"
                      color="fg"
                      borderColor="fg"
                      onClick={() =>
                        append({
                          part_type: 'system',
                          part: 0,
                          manual_part_number: '',
                          qty: 1,
                          unit_price: '1',
                          needed_by: '',
                          notes: '',
                          release: selectedRelease?.id as number,
                        })
                      }
                    >
                      Add line
                    </Button>
                    {isNew && (
                      <Flex gapX={2} flexWrap="wrap" flexDirection="row">
                        <Button
                          size="sm"
                          type="submit"
                          onClick={async () => {
                            if (!trigger || !setValue) return;

                            await trigger().then(
                              (valid) =>
                                valid &&
                                setValue(
                                  'draftOrderSubmissionType',
                                  'saveDraft',
                                ),
                            );
                          }}
                        >
                          Save Draft
                        </Button>

                        {!draftOrderLines?.length ? (
                          <Flex>
                            <PopoverLazy
                              disabled
                              button={
                                <Button
                                  size="sm"
                                  bg="fg"
                                  color="fg.inverted"
                                  onClick={async () => {
                                    if (!trigger || !setValue) return;

                                    await trigger();
                                  }}
                                >
                                  Convert to PO
                                </Button>
                              }
                              popOverTitle=""
                            >
                              <Flex flexDirection="column" gapY={4}>
                                <Text fontWeight="bold" fontSize="md">
                                  Convert to PO
                                </Text>
                                <Text fontSize="sm">
                                  Please add at least one line to convert to PO
                                </Text>
                              </Flex>
                            </PopoverLazy>
                          </Flex>
                        ) : (
                          <Button
                            size="sm"
                            type="submit"
                            bg="fg"
                            color="fg.inverted"
                            onClick={async () => {
                              if (!trigger || !setValue) return;

                              await trigger().then(
                                (valid) =>
                                  valid &&
                                  setValue(
                                    'draftOrderSubmissionType',
                                    'createPo',
                                  ),
                              );
                            }}
                          >
                            Convert to PO
                          </Button>
                        )}
                      </Flex>
                    )}
                  </Flex>

                  {/* Scroll area */}
                  <Box
                    width="full"
                    flex="1"
                    minH="0"
                    overflowY="auto"
                    overflowX="hidden"
                    pr={1}
                  >
                    <Flex width="full" flexDirection="column" gapY={10}>
                      {/* Draft order details */}
                      {isNew && (
                        <Flex width="full" flexDirection="column" gapY={2}>
                          <Text fontWeight="bold" fontSize="md">
                            Draft Order Details
                          </Text>

                          <Flex
                            width="full"
                            flexWrap="wrap"
                            gap={4}
                            alignItems="flex-start"
                          >
                            <Box
                              width={{ base: 'full', md: '360px' }}
                              position="relative"
                              minW={0}
                            >
                              <FormInput
                                control={control}
                                label="Title"
                                id="draftOrder.title"
                                register={register}
                                errors={errors}
                                showLabelAtTop
                                width="full"
                                placeholder="Enter Title"
                                required
                              />
                              <Text
                                position="absolute"
                                bottom={-2}
                                fontSize="xs"
                                color="fg.error"
                              >
                                {errors?.draftOrder?.title?.message}
                              </Text>
                            </Box>
                            <Box
                              width={{ base: 'full', md: '360px' }}
                              position="relative"
                              minW={0}
                            >
                              <FormInput
                                control={control}
                                label="Function Number"
                                id="draftOrder.function_number"
                                register={register}
                                errors={errors}
                                showLabelAtTop
                                width="full"
                                placeholder="Enter Function Number"
                              />
                              <Text
                                position="absolute"
                                bottom={-2}
                                fontSize="xs"
                                color="fg.error"
                              >
                                {errors?.draftOrder?.function_number?.message}
                              </Text>
                            </Box>
                          </Flex>
                        </Flex>
                      )}

                      {/* Draft order lines */}
                      {!!draftOrderLines?.length && (
                        <Flex width="full" flexDirection="column" gapY={2}>
                          {isNew && (
                            <Text fontWeight="bold" fontSize="md">
                              Draft Order Lines Details
                            </Text>
                          )}

                          <Flex width="full" flexDirection="column">
                            {fields.map((field, index) => {
                              return (
                                <Flex
                                  width="full"
                                  flexDirection="column"
                                  key={`draft-order-lines-${field.id}`}
                                >
                                  <DraftOrderLineRow
                                    register={register}
                                    control={control}
                                    errors={errors}
                                    index={index}
                                    draftOrderLines={draftOrderLines}
                                    remove={remove}
                                    setValue={setValue}
                                    trigger={trigger}
                                    releases={filteredReleasesByProject ?? []}
                                    selectedProjectId={
                                      selectedProject?.id as number
                                    }
                                  />
                                  {index !== fields.length - 1 && (
                                    <Separator marginBottom={4} />
                                  )}
                                </Flex>
                              );
                            })}
                          </Flex>
                        </Flex>
                      )}
                    </Flex>
                  </Box>
                </Flex>
              </Flex>
            );
          }}
        </FormController>
      </Flex>
    </Card>
  );
};

export default DraftOrderManageCard;

const draftOrderLineSchema = z
  .object({
    ...ProcurementType.DraftOrderLines.draftOrderLineBaseSchema.shape,
    part_type: z.enum(['system', 'manual']),
    part: z.preprocess((val) => {
      // Treat empty values or 0 as undefined
      if (val === '' || val === null || val === undefined || val === 0)
        return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    }, z.number().positive().optional()),
    manual_part_number: z.string().optional(),
  })
  .refine(
    (line) =>
      line.part_type !== 'system' || (line.part != null && line.part > 0),
    {
      message: 'Part is required',
      path: ['part'],
    },
  )
  .refine(
    (line) =>
      line.part_type !== 'manual' ||
      (line.manual_part_number != null &&
        line.manual_part_number.trim() !== ''),
    {
      message: 'Manual part number is required',
      path: ['manual_part_number'],
    },
  );

const draftOrdersFormSchemaPost = z.object({
  draftOrderSubmissionType: z.enum(['saveDraft', 'createPo']),
  draftOrder: ProcurementType.DraftOrders.post.body,
  draftOrderLines: z.array(draftOrderLineSchema).optional(),
});
const draftOrdersFormSchemaPatch = z.object({
  draftOrderSubmissionType: z.enum(['saveDraft', 'createPo']),
  draftOrder: ProcurementType.DraftOrders.post.body.nullable(),
  draftOrderLines: z.array(draftOrderLineSchema).optional(),
});

type DraftOrdersFormType =
  | z.infer<typeof draftOrdersFormSchemaPost>
  | z.infer<typeof draftOrdersFormSchemaPatch>;

// Draft order line row
type DraftOrderLineRowProps = {
  register: UseFormRegister<DraftOrdersFormType>;
  control: Control<DraftOrdersFormType>;
  errors: FieldErrors<DraftOrdersFormType>;
  index: number;
  draftOrderLines: DraftOrdersFormType['draftOrderLines'];
  remove: UseFieldArrayRemove;
  setValue?: UseFormReturn<DraftOrdersFormType>['setValue'];
  trigger?: UseFormReturn<DraftOrdersFormType>['trigger'];
  releases: ReleasesType.Release[];
  selectedProjectId: number;
};

const DraftOrderLineRow = ({
  register,
  control,
  errors,
  index,
  draftOrderLines,
  remove,
  setValue,
  trigger,
  releases,
  selectedProjectId,
}: DraftOrderLineRowProps) => {
  const watchedRelease = draftOrderLines?.[index]?.release;

  const partsSelectedInDraftOrderLineForm =
    draftOrderLines
      ?.filter((_, i) => i !== index)
      ?.filter(
        (line) =>
          line.part_type === 'system' && line.release === watchedRelease,
      )
      ?.map((line) => line.part)
      ?.filter((v): v is number => v !== undefined && v > 0) ?? [];

  const existingAddedDraftOrderLineParts = new Set<number>([
    ...partsSelectedInDraftOrderLineForm,
  ]);

  const { data: getPartsListByProjectAndReleaseQuery } =
    CoreApi.Api.useGetPartsListByProjectAndReleaseQuery({
      query: {
        project: selectedProjectId,
        release: watchedRelease,
      },
    });

  const filteredParts = getPartsListByProjectAndReleaseQuery?.filter(
    (part) => !existingAddedDraftOrderLineParts.has(part.id),
  );

  const partType = draftOrderLines?.[index]?.part_type;

  return (
    <Flex width="full" flexDirection="column">
      <Box maxWidth="300px">
        <FormSelect
          maxWidth="300px"
          control={control}
          register={register}
          errors={errors}
          id={`draftOrderLines.${index}.part_type`}
          label="Part Type"
          items={[
            {
              label: 'System Part',
              value: 'system',
            },
            {
              label: 'Manual Part',
              value: 'manual',
            },
          ]}
          keyForDisplay="label"
          keyForValue="value"
          showLabelAtTop
          required
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const changedPartType = e.target.value as 'system' | 'manual';

            if (!setValue || !trigger) return;

            if (changedPartType === 'system') {
              // switching to system: require part, clear manual
              setValue(`draftOrderLines.${index}.manual_part_number`, '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue(`draftOrderLines.${index}.part`, 0 as any, {
                // let preprocess turn 0 -> undefined until selected
                shouldDirty: true,
                shouldValidate: true,
              });
            } else {
              // switching to manual: require manual_part_number, clear part
              setValue(`draftOrderLines.${index}.part`, undefined, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue(`draftOrderLines.${index}.manual_part_number`, '', {
                shouldDirty: true,
                shouldValidate: true,
              });
            }

            // IMPORTANT: ensure RHF actually updates part_type
            setValue(`draftOrderLines.${index}.part_type`, changedPartType, {
              shouldDirty: true,
              shouldValidate: true,
            });

            // trigger relevant fields so UI/backend payload matches the selection
            trigger([
              `draftOrderLines.${index}.part_type`,
              `draftOrderLines.${index}.part`,
              `draftOrderLines.${index}.manual_part_number`,
            ] as any);
          }}
        />
      </Box>

      <Flex width="full" paddingY={4} gap={4} flexDirection="column">
        <Text fontWeight="bold" color="tertiary.fg">
          Line: {index + 1}
        </Text>

        <Flex gapX={4}>
          <Box
            key={`release-${index}-${partType}`}
            position="relative"
            maxW="250px"
          >
            <FormComboBox
              maxW="250px"
              register={register}
              control={control}
              errors={errors}
              id={`draftOrderLines.${index}.release`}
              label="Release (optional)"
              keyForDisplay="number"
              keyForValue="id"
              items={releases ?? []}
              showLabelAtTop
              clearable
            />
            <Text
              position="absolute"
              bottom={-2}
              fontSize="xs"
              color="fg.error"
            >
              {errors?.draftOrderLines?.[index]?.release?.message}
            </Text>
          </Box>

          {/* Part */}
          {partType === 'system' ? (
            <Box
              key={`allowed-system-parts-${index}-${partType}`}
              position="relative"
              maxW="250px"
            >
              <FormComboBox
                maxW="250px"
                register={register}
                control={control}
                errors={errors}
                id={`draftOrderLines.${index}.part`}
                label="Part"
                keyForDisplay="part_number"
                keyForValue="id"
                items={filteredParts ?? []}
                showLabelAtTop
                required
              />
              <Text
                position="absolute"
                bottom={-2}
                fontSize="xs"
                color="fg.error"
              >
                {errors?.draftOrderLines?.[index]?.part?.message}
              </Text>
            </Box>
          ) : (
            <Box
              key={`manual-parts-${index}-${partType}`}
              position="relative"
              maxW="250px"
            >
              <FormInput
                maxW="250px"
                register={register}
                control={control}
                errors={errors}
                id={`draftOrderLines.${index}.manual_part_number`}
                label="Manual Part Number"
                showLabelAtTop
                required
              />
              <Text
                position="absolute"
                bottom={-2}
                fontSize="xs"
                color="fg.error"
              >
                {errors?.draftOrderLines?.[index]?.manual_part_number?.message}
              </Text>
            </Box>
          )}

          {/* Needed by */}
          <Box position="relative" minW={0} maxWidth="250px">
            <FormInput
              maxWidth="250px"
              register={register}
              control={control}
              errors={errors}
              id={`draftOrderLines.${index}.needed_by`}
              label="Needed By"
              showLabelAtTop
              type="date"
              required
            />
            <Text
              position="absolute"
              bottom={-2}
              fontSize="xs"
              color="fg.error"
            >
              {errors?.draftOrderLines?.[index]?.needed_by?.message}
            </Text>
          </Box>

          {/*   Qty   */}
          <Box position="relative" maxWidth="100px">
            <FormInput
              maxWidth="100px"
              register={register}
              control={control}
              errors={errors}
              id={`draftOrderLines.${index}.qty`}
              label="Qty"
              showLabelAtTop
              type="number"
              min={1}
            />
            <Text
              position="absolute"
              bottom={-2}
              fontSize="xs"
              color="fg.error"
            >
              {errors?.draftOrderLines?.[index]?.qty?.message}
            </Text>
          </Box>

          {/*   Unit Price   */}
          <Box position="relative" maxWidth="100px">
            <FormInput
              maxWidth="100px"
              register={register}
              control={control}
              errors={errors}
              id={`draftOrderLines.${index}.unit_price`}
              label="Unit Price"
              showLabelAtTop
              type="number"
              min={1}
            />
            <Text
              position="absolute"
              bottom={-2}
              fontSize="xs"
              color="fg.error"
            >
              {errors?.draftOrderLines?.[index]?.unit_price?.message}
            </Text>
          </Box>
        </Flex>

        <FormTextArea
          register={register}
          control={control}
          errors={errors}
          id={`draftOrderLines.${index}.notes`}
          label="Notes (Optional)"
          showLabelAtTop
          minH="2lh"
          maxH="2lh"
        />

        <Flex justifyContent="flex-end">
          <Button
            width="fit-content"
            marginTop={1}
            colorPalette="red"
            onClick={() => remove(index)}
          >
            Remove
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
